import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, NgZone, OnChanges, OnDestroy, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { Subscription } from 'rxjs';

import { DOCTORS } from './data/doctors.data';
import { PAGE_META } from './data/page-meta';
import { SAMPLE_REPORTS } from './data/sample-reports.data';
import { SPECIALTIES } from './data/specialties.data';
import { SiteDepartmentService } from './site-department.service';
import { SiteDoctorService } from './site-doctor.service';
import { DEPARTMENT_SLUG_PREFIX, DOCTOR_SLUG_PREFIX } from './site-utils';
import { SiteScrollReveal } from './site-scroll-reveal';
import { SiteNavService } from './site-nav.service';

const FONTS_LINK_ID = 'ws-site-fonts';
const FONTS_URL = 'https://fonts.googleapis.com/css2?family=Poppins:wght@500;600;700&family=Inter:wght@400;500;600;700&display=swap';

@Component({
  selector: 'ngx-well-spring-site',
  templateUrl: './well-spring-site.component.html',
  styleUrls: ['./well-spring-site.component.css'],
  encapsulation: ViewEncapsulation.ShadowDom,
  providers: [SiteNavService, SiteDepartmentService, SiteDoctorService]
})
export class WellSpringSiteComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() showCareNavigator = true;
  @Input() tenantId: string;
  @Input() branchId: string;
  @Input() headerOnly = false;
  @Input() initialPage: string | null = null;
  @Input() logoSrc: string | null = null;
  @Input() logoAlt = 'WellSpring';
  @Input() logoPending = false;
  readonly defaultLogoSrc = 'assets/images/well-spring-site/Wellspring-logo.svg';
  @Output() loginSelect = new EventEmitter<string>();
  @Output() pageSelect = new EventEmitter<string>();
  @Output() signupSelect = new EventEmitter<void>();

  kind = 'home';
  nav = 'index';
  navOpen = false;

  private reveal: SiteScrollReveal;
  private headerEl: HTMLElement | null = null;
  private lastY = 0;
  private ticking = false;
  private currentSlug = '';
  private readonly subs = new Subscription();
  private readonly onScroll = () => {
    if (!this.ticking) { this.ticking = true; window.requestAnimationFrame(this.updateHeader); }
  }

  constructor(private host: ElementRef<HTMLElement>, private siteNav: SiteNavService, private departments: SiteDepartmentService, private doctors: SiteDoctorService, private zone: NgZone) {}

  ngOnChanges() {
    if (this.headerOnly) { return; }
    this.departments.configure(this.tenantId, this.branchId);
    this.doctors.configure(this.tenantId, this.branchId);
  }

  ngOnInit() {
    this.loadFonts();
    this.subs.add(this.siteNav.loginRequest$.subscribe(type => this.loginSelect.emit(type)));
    this.subs.add(this.siteNav.signupRequest$.subscribe(() => this.signupSelect.emit()));
    if (this.headerOnly) { this.nav = ''; } else if (this.initialPage) { this.siteNav.go('/' + this.initialPage); }
    let firstPage = true;
    this.subs.add(this.siteNav.page$.subscribe(page => {
      if (this.headerOnly) {
        if (!firstPage) { this.pageSelect.emit(page.slug === 'index' ? 'index' : page.slug); }
        firstPage = false;
        return;
      }
      const slug = this.resolveSlug(page.slug);
      const samePage = slug === this.currentSlug;
      this.currentSlug = slug;
      this.kind = this.kindOf(slug);
      const meta = PAGE_META[slug];
      this.nav = meta ? meta.nav : (slug.indexOf(DOCTOR_SLUG_PREFIX) === 0 || slug.indexOf(DEPARTMENT_SLUG_PREFIX) === 0 ? 'doctors' : '');
      this.applyBodyClass(meta ? meta.bodyClass : '');
      this.navOpen = false;
      setTimeout(() => {
        this.scrollTo(page.fragment);
        if (!samePage) { this.reveal.refresh(); }
      });
    }));
  }

  ngAfterViewInit() {
    const root = this.host.nativeElement.shadowRoot;
    this.reveal = new SiteScrollReveal(root, this.host.nativeElement);
    this.headerEl = root.querySelector<HTMLElement>('.site-header');
    this.lastY = window.scrollY;
    this.updateHeader();
    this.reveal.refresh();
    this.zone.runOutsideAngular(() => window.addEventListener('scroll', this.onScroll, { passive: true }));
  }

  ngOnDestroy() {
    window.removeEventListener('scroll', this.onScroll);
    if (this.reveal) { this.reveal.disconnect(); }
    this.subs.unsubscribe();
  }

  toggleNav() { this.navOpen = !this.navOpen; }
  closeNav() { this.navOpen = false; }

  chooseLogin(type: string, event: Event) {
    event.preventDefault();
    this.loginSelect.emit(type);
  }

  @HostListener('document:keydown.escape')
  onEscape() { this.closeNav(); }

  @HostListener('focusin')
  onFocusIn() { if (this.headerEl) { this.headerEl.classList.remove('header-hidden'); } }

  private resolveSlug(slug: string): string {
    if (slug === 'index' || ['how-it-works', 'services', 'doctors', 'faq', 'contact'].indexOf(slug) > -1) { return slug; }
    if (slug.indexOf(DOCTOR_SLUG_PREFIX) === 0 || slug.indexOf(DEPARTMENT_SLUG_PREFIX) === 0) { return slug; }
    if (SPECIALTIES.some(s => s.slug === slug) || DOCTORS.some(d => d.slug === slug) || SAMPLE_REPORTS.some(r => r.slug === slug)) { return slug; }
    return 'index';
  }

  private kindOf(slug: string): string {
    if (slug === 'index') { return 'home'; }
    if (SPECIALTIES.some(s => s.slug === slug)) { return 'specialty'; }
    if (DOCTORS.some(d => d.slug === slug) || slug.indexOf(DOCTOR_SLUG_PREFIX) === 0) { return 'doctor-profile'; }
    if (slug.indexOf(DEPARTMENT_SLUG_PREFIX) === 0) { return 'department'; }
    if (SAMPLE_REPORTS.some(r => r.slug === slug)) { return 'sample-report'; }
    return slug;
  }

  private applyBodyClass(bodyClass: string) {
    const el = this.host.nativeElement;
    Array.prototype.slice.call(el.classList)
      .filter((c: string) => c.indexOf('page-') === 0)
      .forEach((c: string) => el.classList.remove(c));
    (bodyClass || '').split(/\s+/).filter(Boolean).forEach(c => el.classList.add(c));
  }

  private scrollTo(fragment: string | null) {
    const target = fragment ? this.host.nativeElement.shadowRoot.getElementById(fragment) : null;
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      window.scrollTo(0, 0);
    }
  }

  private loadFonts() {
    if (document.getElementById(FONTS_LINK_ID)) { return; }
    const link = document.createElement('link');
    link.id = FONTS_LINK_ID;
    link.rel = 'stylesheet';
    link.href = FONTS_URL;
    document.head.appendChild(link);
  }

  private menuIsOpen() {
    return !!this.host.nativeElement.shadowRoot.querySelector('.nav-links.open, .site-header .cta-chooser.open');
  }

  private readonly updateHeader = () => {
    const header = this.headerEl;
    if (!header) { this.ticking = false; return; }
    const y = Math.max(window.scrollY, 0);
    const dy = y - this.lastY;
    header.classList.toggle('is-scrolled', y > 8);
    if (y <= header.offsetHeight + 40 || this.menuIsOpen()) {
      header.classList.remove('header-hidden');
      this.lastY = y;
    } else if (dy > 6) {
      header.classList.add('header-hidden');
      this.lastY = y;
    } else if (dy < -6) {
      header.classList.remove('header-hidden');
      this.lastY = y;
    }
    this.ticking = false;
  }
}
